import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { Job } from '@workflow/contracts'
import { cn, COLLAPSE, DURATION, Icons } from '@workflow/ui'
import { countOf } from '@/lib/format'
import { JobRow } from './JobRow'
import { JobQuickView } from './JobQuickView'
import type { JobGroup } from './jobPeriods'

/**
 * The work in folds, each a line with a count that opens and closes with a
 * press: the newest period open, the rest closed unless `openAll` says so.
 */
export function JobGroups({ groups, lead, openAll = false }: {
  groups: JobGroup[]
  lead?: { label: string; jobs: Job[] }
  /** Days picked by hand all start open: the choosing already narrowed them. */
  openAll?: boolean
}) {
  const first = groups.find((group) => group.current)?.key ?? groups[0]?.key
  // Only what the viewer changed is kept; everything else follows the default.
  const [toggled, setToggled] = useState<Record<string, boolean>>({})
  const isOpen = (key: string, fallback: boolean) => toggled[key] ?? fallback
  // One quick view for the whole list; it reads the job afresh from the
  // groups, so a move made inside it shows at once.
  const [viewing, setViewing] = useState<Job | null>(null)
  const all = [...(lead?.jobs ?? []), ...groups.flatMap((group) => group.jobs)]
  const current = viewing ? all.find((job) => job.id === viewing.id) ?? viewing : null
  const flip = (key: string, fallback: boolean) => setToggled((all) => ({ ...all, [key]: !isOpen(key, fallback) }))

  return (
    <div className="flex flex-col">
      {lead && lead.jobs.length > 0 ? (
        <Fold
          label={lead.label}
          count={lead.jobs.length}
          open={isOpen('lead', true)}
          onToggle={() => flip('lead', true)}
          jobs={lead.jobs}
          onOpen={setViewing}
        />
      ) : null}
      {groups.map((group) => (
        <Fold
          key={group.key}
          label={group.label}
          detail={group.detail}
          count={group.jobs.length}
          open={isOpen(group.key, openAll || group.key === first)}
          onToggle={() => flip(group.key, openAll || group.key === first)}
          jobs={group.jobs}
          onOpen={setViewing}
        />
      ))}
      <JobQuickView job={current} onClose={() => setViewing(null)} />
    </div>
  )
}

function Fold({ label, detail, count, open, onToggle, jobs, onOpen }: {
  label: string
  detail?: string | null
  count: number
  open: boolean
  onToggle: () => void
  jobs: Job[]
  onOpen: (job: Job) => void
}) {
  const [moving, setMoving] = useState(false)
  const settle = useRef<number | null>(null)
  useEffect(() => () => { if (settle.current !== null) window.clearTimeout(settle.current) }, [])
  return (
    <section className="border-b border-hairline last:border-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setMoving(true)
          if (settle.current !== null) window.clearTimeout(settle.current)
          settle.current = window.setTimeout(() => setMoving(false), DURATION.base * 1000 + 50)
          onToggle()
        }}
        className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-lg px-2 py-3.5 text-left outline-none transition-colors duration-fast hover:bg-tint focus-visible:ring-1 focus-visible:ring-foreground"
      >
        <span className={cn('flex text-muted-foreground transition-transform duration-fast', !open && '-rotate-90')}>
          <Icons.dropdown size="sm" />
        </span>
        <span className="min-w-0 flex-1 truncate">
          <span className="text-ui font-medium text-foreground">{label}</span>
          {detail ? <span className="ml-2 text-caption text-muted-foreground">{detail}</span> : null}
        </span>
        <span className="shrink-0 text-caption text-muted-foreground tabular-nums">{countOf(count, 'job')}</span>
      </button>
      {/* The fold clips only while it moves, so a row's menus can hang past its
          last row once it rests. The clip sits on a wrapper because a leaving
          list keeps the classes it had; its side room keeps the rows' rounded hover whole. */}
      <div className={cn('-mx-2 px-2', moving && 'overflow-hidden')}>
        <AnimatePresence initial={false}>
          {open ? (
            <motion.ul {...COLLAPSE} className="flex flex-col">
              {jobs.map((job) => <JobRow key={job.id} job={job} onOpen={onOpen} />)}
            </motion.ul>
          ) : null}
        </AnimatePresence>
      </div>
    </section>
  )
}
